import React, { useState, useEffect } from 'react'
import { Select, SelectOptionType } from '@eltoro-ui/components'
import { useReportHub } from '../../../Contexts/useReportHub'
import { TimeframeOption } from '../types'

export const timeframeOptions: SelectOptionType[] = [
  { label: 'Lifetime', value: 'lifetime' },
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This Week', value: 'this-week' },
  { label: 'Last Week', value: 'last-week' },
  { label: 'MTD', value: 'mtd' },
  { label: 'Last Month', value: 'last-month' },
  { label: 'QTD', value: 'qtd' },
  { label: 'Last Quarter', value: 'last-quarter' },
  { label: 'Custom', value: 'custom' },
]

export const ReportHubTimeframeSelector: React.FC = () => {
  const {
    filters,
    updateTimeframe,
    handleTimeframeChange: handleTimeframeChangeContext,
    isLoading,
  } = useReportHub()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [debouncedStartDate, setDebouncedStartDate] = useState('')
  const [debouncedEndDate, setDebouncedEndDate] = useState('')
  const [dateError, setDateError] = useState<string | null>(null)
  // Separate timers for start date to prevent race conditions
  const [
    clearStartInputTimer,
    setClearStartInputTimer,
  ] = useState<NodeJS.Timeout | null>(null)
  const [
    clearStartErrorTimer,
    setClearStartErrorTimer,
  ] = useState<NodeJS.Timeout | null>(null)
  // Separate timers for end date to prevent race conditions
  const [
    clearEndInputTimer,
    setClearEndInputTimer,
  ] = useState<NodeJS.Timeout | null>(null)
  const [
    clearEndErrorTimer,
    setClearEndErrorTimer,
  ] = useState<NodeJS.Timeout | null>(null)

  // Find the currently selected option
  const selectedOption = timeframeOptions.find(
    (option) =>
      option.value === filters.timeframe.toLowerCase().replace(' ', '-'),
  )

  const validateDateRange = (start: string, end: string): string | null => {
    if (!start || !end) return null

    const startDateObj = new Date(start)
    const endDateObj = new Date(end)

    // Get today's date as a date string for proper comparison
    const today = new Date()
    const [todayDateString] = today.toISOString().split('T') // YYYY-MM-DD format
    const todayDateObj = new Date(todayDateString)

    // Check if start date is in the future (compare date values, not datetime)
    if (startDateObj > todayDateObj) {
      return 'Start date cannot be in the future'
    }

    // Check if end date is in the future (compare date values, not datetime)
    if (endDateObj > todayDateObj) {
      return 'End date cannot be in the future'
    }

    // Check if start date is after end date
    if (startDateObj > endDateObj) {
      return 'Start date cannot be after end date'
    }

    return null
  }

  // Validate individual date for immediate feedback
  const validateSingleDate = (
    dateValue: string,
    otherDateValue: string,
    isStartDate: boolean,
  ): string | null => {
    if (!dateValue) return null

    const dateObj = new Date(dateValue)

    // Get today's date as a date string for proper comparison
    const today = new Date()
    const [todayDateString] = today.toISOString().split('T') // YYYY-MM-DD format
    const todayDateObj = new Date(todayDateString)

    // Check if date is in the future (compare date values, not datetime)
    if (dateObj > todayDateObj) {
      return `${isStartDate ? 'Start' : 'End'} date cannot be in the future`
    }

    // If both dates are present, check the range
    if (otherDateValue) {
      return validateDateRange(
        isStartDate ? dateValue : otherDateValue,
        isStartDate ? otherDateValue : dateValue,
      )
    }

    return null
  }

  // Debounce start date changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStartDate(startDate)
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [startDate])

  // Debounce end date changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEndDate(endDate)
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [endDate])

  // Handle debounced date changes and trigger API calls
  useEffect(() => {
    if (!debouncedStartDate || !debouncedEndDate) return

    const error = validateDateRange(debouncedStartDate, debouncedEndDate)
    setDateError(error)

    if (!error) {
      updateTimeframe('Custom')
      handleTimeframeChangeContext('Custom', {
        startDate: debouncedStartDate,
        endDate: debouncedEndDate,
      })
    }
  }, [
    debouncedStartDate,
    debouncedEndDate,
    updateTimeframe,
    handleTimeframeChangeContext,
  ])

  const handleTimeframeChange = (option: SelectOptionType | undefined) => {
    if (!option?.value) return

    // Clear date error when switching away from custom
    if (option.value !== 'custom') {
      setDateError(null)
    }

    // Convert option value back to TimeframeOption format
    const timeframeMap: Record<string, TimeframeOption> = {
      lifetime: 'Lifetime',
      today: 'Today',
      yesterday: 'Yesterday',
      'this-week': 'This Week',
      'last-week': 'Last Week',
      mtd: 'MTD',
      'last-month': 'Last Month',
      qtd: 'QTD',
      'last-quarter': 'Last Quarter',
      custom: 'Custom',
    }

    const timeframe = timeframeMap[option.value as string]
    if (timeframe) {
      // For custom timeframe, set pending state but update UI timeframe
      if (timeframe === 'Custom') {
        updateTimeframe(timeframe) // Update to show UI, but mark as pending
        return
      }
      handleTimeframeChangeContext(timeframe)
    }
  }

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newStartDate = event.target.value
    setStartDate(newStartDate)

    // Clear any pending timers for start date when user types a new value
    if (clearStartInputTimer) clearTimeout(clearStartInputTimer)
    if (clearStartErrorTimer) clearTimeout(clearStartErrorTimer)

    // Immediate validation for UI feedback, but don't trigger API calls
    const error = validateSingleDate(newStartDate, endDate, true)
    setDateError(error)

    // If there's an error, clear the invalid date after a short delay
    // But keep the error message visible longer
    if (error) {
      const inputTimer = setTimeout(() => {
        setStartDate('')
        setDebouncedStartDate('')
      }, 2000) // Clear input after 2 seconds
      setClearStartInputTimer(inputTimer)

      const errorTimer = setTimeout(() => {
        setDateError(null)
      }, 8000) // Clear error message after 8 seconds
      setClearStartErrorTimer(errorTimer)
    } else {
      // If date is now valid, clear timers
      setClearStartInputTimer(null)
      setClearStartErrorTimer(null)
    }
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = event.target.value
    setEndDate(newEndDate)

    // Clear any pending timers for end date when user types a new value
    if (clearEndInputTimer) clearTimeout(clearEndInputTimer)
    if (clearEndErrorTimer) clearTimeout(clearEndErrorTimer)

    // Immediate validation for UI feedback, but don't trigger API calls
    const error = validateSingleDate(newEndDate, startDate, false)
    setDateError(error)

    // If there's an error, clear the invalid date after a short delay
    // But keep the error message visible longer
    if (error) {
      const inputTimer = setTimeout(() => {
        setEndDate('')
        setDebouncedEndDate('')
      }, 2000) // Clear input after 2 seconds
      setClearEndInputTimer(inputTimer)

      const errorTimer = setTimeout(() => {
        setDateError(null)
      }, 8000) // Clear error message after 8 seconds
      setClearEndErrorTimer(errorTimer)
    } else {
      // If date is now valid, clear timers
      setClearEndInputTimer(null)
      setClearEndErrorTimer(null)
    }
  }

  const isCustomSelected = filters.timeframe === 'Custom'

  // Get today's date in YYYY-MM-DD format for max attribute
  const [todayString] = new Date().toISOString().split('T')

  return (
    <section
      className="ReportHubTimeframeSelector w-full self-end lg:w-auto"
      aria-labelledby="timeframe-title"
    >
      <h3
        id="timeframe-title"
        className="text-tint-gray-900 mb-3 font-semibold sm:text-lg"
      >
        Choose Timeframe
      </h3>

      <div
        className="bg-base border-tint-gray-300 rounded-lg border p-3 sm:p-4"
        role="group"
        aria-labelledby="timeframe-title"
      >
        <div className="flex items-center gap-4">
          <Select
            placeholder="Select timeframe"
            options={timeframeOptions}
            selectedOption={selectedOption}
            onChange={handleTimeframeChange}
            maxHeight={250}
            clearButton={false}
            className="w-full min-w-[160px]"
            selectClassName="py-3"
            aria-label="Select time period for dashboard data"
            aria-describedby="timeframe-help"
            disabled={isLoading}
          />
          <div id="timeframe-help" className="sr-only">
            Select a predefined time period or choose Custom to set specific
            dates
          </div>

          {isCustomSelected && (
            <div
              className="flex flex-row items-center gap-2 sm:gap-4"
              role="group"
              aria-label="Custom date range selection"
            >
              <div className="flex flex-1 flex-col">
                <label htmlFor="start-date" className="sr-only">
                  Start date
                </label>
                <input
                  id="start-date"
                  type="date"
                  value={startDate}
                  max={todayString}
                  onChange={handleStartDateChange}
                  placeholder="mm/dd/yyyy"
                  className={`border-tint-gray-300 w-full rounded-md border px-2 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:px-3 sm:text-sm ${
                    dateError
                      ? 'border-warning-500 focus:border-warning-500 focus:ring-warning-500'
                      : ''
                  }`}
                  aria-label="Start date for custom range"
                  aria-describedby="date-range-help date-error"
                  aria-invalid={!!dateError}
                />
              </div>

              <span
                className="text-tint-gray-500 text-xs sm:text-sm"
                aria-hidden="true"
              >
                to
              </span>

              <div className="flex flex-1 flex-col">
                <label htmlFor="end-date" className="sr-only">
                  End date
                </label>
                <input
                  id="end-date"
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  max={todayString}
                  onChange={handleEndDateChange}
                  placeholder="mm/dd/yyyy"
                  className={`border-tint-gray-300 w-full rounded-md border px-2 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:px-3 sm:text-sm ${
                    dateError
                      ? 'border-warning-500 focus:border-warning-500 focus:ring-warning-500'
                      : ''
                  }`}
                  aria-label="End date for custom range"
                  aria-describedby="date-range-help date-error"
                  aria-invalid={!!dateError}
                />
              </div>
              <div id="date-range-help" className="sr-only">
                Select start and end dates for your custom time period
              </div>
            </div>
          )}
        </div>

        {isCustomSelected && dateError && (
          <div
            id="date-error"
            className="text-warning-600 mt-2 flex flex-row-reverse text-sm font-bold"
            role="alert"
            aria-live="polite"
          >
            {dateError}
          </div>
        )}
      </div>
    </section>
  )
}
